import type { WorkTask } from '../../db/work-task-types.js';
import type { WorkTaskEnglishTranslation } from '../../db/work-task-translation-types.js';
export type TaskTranslationEntry = {
  phase: 'loading' | 'ready' | 'error';
  value?: WorkTaskEnglishTranslation;
  error?: string;
};
/** A view-only translation queue. In-flight requests are bounded even across locale changes. */
export function createTaskTranslationQueue(
  request: (task: WorkTask) => Promise<WorkTaskEnglishTranslation>,
  changed: () => void,
) {
  const entries = new Map<string, TaskTranslationEntry>();
  const running = new Set<string>();
  let desired = new Map<string, WorkTask>();
  let enabled = false,
    disposed = false,
    active = 0,
    epoch = 0;
  const key = (task: WorkTask) => `${task._id}:${task.revision}`;
  const valid = (task: WorkTask, result: WorkTaskEnglishTranslation) =>
    result?.taskID === task._id &&
    result.revision === task.revision &&
    result.language === 'en' &&
    typeof result.title === 'string' &&
    typeof result.description === 'string' &&
    typeof result.deliveryEstimate === 'string' &&
    (task.testInstructions
      ? typeof result.testInstructions === 'string' && !!result.testInstructions.trim()
      : result.testInstructions === undefined || result.testInstructions === '') &&
    Array.isArray(result.feedbackLinks) &&
    result.feedbackLinks.length === task.feedbackLinks.length &&
    result.feedbackLinks.every(
      (link, index) =>
        link.callID === task.feedbackLinks[index]?.callID &&
        link.testerName === task.feedbackLinks[index]?.testerName &&
        typeof link.notesPreview === 'string' &&
        (task.feedbackLinks[index]?.notes === undefined ? link.notes === undefined : typeof link.notes === 'string'),
    );
  const current = (id: string, version: number) => !disposed && enabled && version === epoch && desired.has(id);
  const pump = () => {
    if (disposed || !enabled) return;
    for (const [id, task] of desired) {
      if (active >= 2) break;
      if (entries.has(id) || running.has(id)) continue;
      const version = epoch;
      active++;
      running.add(id);
      entries.set(id, { phase: 'loading' });
      changed();
      void (async () => {
        try {
          const value = await request(task);
          if (!valid(task, value)) throw new Error('TASK_TRANSLATION_INVALID_RESPONSE');
          if (current(id, version)) entries.set(id, { phase: 'ready', value });
        } catch (error) {
          if (current(id, version))
            entries.set(id, { phase: 'error', error: error instanceof Error ? error.message : String(error) });
        } finally {
          active--;
          running.delete(id);
          if (!current(id, version) && entries.get(id)?.phase === 'loading') entries.delete(id);
          if (!disposed) {
            changed();
            pump();
          }
        }
      })();
    }
  };
  return {
    sync(language: 'de' | 'en', tasks: readonly WorkTask[]) {
      if (disposed) return;
      const nextEnabled = language === 'en';
      if (nextEnabled !== enabled) epoch++;
      enabled = nextEnabled;
      desired = new Map(tasks.map(task => [key(task), task]));
      // Old revisions cannot be displayed or retained indefinitely.
      for (const id of entries.keys()) if (!desired.has(id) && !running.has(id)) entries.delete(id);
      if (enabled) pump();
    },
    get(task: WorkTask): TaskTranslationEntry | undefined {
      return entries.get(key(task));
    },
    retry(task: WorkTask) {
      const id = key(task);
      if (!disposed && enabled && !running.has(id)) {
        entries.delete(id);
        pump();
      }
    },
    dispose() {
      disposed = true;
      enabled = false;
      epoch++;
      desired.clear();
      entries.clear();
    },
  };
}
